/* eslint-disable no-shadow */
import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ConfirmationPage from './ConfirmationPage';
import PathBuilderContext from '../WidgetApp/PathBuilderContext';
import IframeHostContext from '../../contexts/iframeHost';
import { getURLSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import { getUserReservations, handleReservationCancel, getNotify } from '../../../../../ui-kitchen/src/api/api';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { reservationsTransform } from '../../../../../ui-kitchen/src/api/resyApiTransformer';
import { reset } from '../../../../../ui-kitchen/src/reducers/bookingSlice';
import getLocationSlug from '../../../../../ui-kitchen/src/utils/getLocationSlug';
import { resetReservationToBeModified, setReservationToBeModified } from '../../../../../ui-kitchen/src/reducers/userSlice';
import { setNavSubTitle, setNavTitle, setRouteEventPath } from '../../../../../ui-kitchen/src/reducers/resyRouterSlice';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

// on desktop, in iFrame
function closeWidget() {
  window.parent.postMessage({
    event: 'close',
    value: true,
  }, '*');
}

// on mobile, in separate browser window
function closeWindow() {
  window.close();
}
const POST_BOOKING = 'post_booking';

const getReturnUrlFromHashQuery = () => {
  const hashQuery = window.location.hash.split('?')[1];
  if (!hashQuery) {
    return null;
  }
  return new URLSearchParams(hashQuery).get('returnUrl');
};

const ConfirmationController = (props) => {
  const {
    Analytics, isAuthenticated, preferredLanguage, reset, resetReservationToBeModified,
    setNavSubTitle, setNavTitle, setReservationToBeModified, setRouteEventPath, returnUrlFromBookingMetadata,
  } = props;

  const searchParams = getURLSearchParams();
  const src = searchParams.get('src') || 'widget';
  const resyToken = searchParams.get('resyToken');
  const conciergeResyToken = searchParams.get('conciergeResyToken');
  const returnUrlFromSearch = searchParams.get('returnUrl');
  const returnUrlFromHashQuery = getReturnUrlFromHashQuery();
  const [initialReturnUrl] = useState(returnUrlFromSearch || returnUrlFromHashQuery || returnUrlFromBookingMetadata);
  const returnUrl = returnUrlFromSearch || returnUrlFromHashQuery || returnUrlFromBookingMetadata || initialReturnUrl;

  const pathBuilder = useContext(PathBuilderContext);
  pathBuilder.setForceSummary(false);

  const iframeHost = useContext(IframeHostContext);
  const viewPortHeight = iframeHost.getDimensions().innerHeight;

  const [reservation, setReservation] = useState(undefined);
  const [notifies, setNotifies] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    let ignore = false;
    const fetchReservation = () => {
      const token = resyToken || conciergeResyToken;
      const bookOnBehalfOf = !!conciergeResyToken;
      const tokenKey = bookOnBehalfOf ? 'concierge_resy_token' : 'resy_token';
      getUserReservations({ [tokenKey]: token, book_on_behalf_of: bookOnBehalfOf })
        .then((data) => {
          if (!ignore) {
            const reservationData = reservationsTransform(data);
            setReservation(reservationData[0]);
            setReservationToBeModified(reservationData[0]);

            if (reservationData[0]?.event?.eventUrlSlug) {
              const venue = reservationData[0]?.venue;
              const locationSlug = getLocationSlug(venue?.location);
              const eventPath = `/cities/${locationSlug}/venues/${venue?.venueSlug}/events/${reservationData[0].event.eventUrlSlug}`;

              pathBuilder.setValues({
                addOnsTemplateId: undefined,
                eventPath,
                venueId: venue?.id,
              });
              setRouteEventPath(pathBuilder.backPath());
            }
          }
        })
        .catch(() => { Logger.error('[ConfirmationController] error'); });
    };
    if (!reservation) {
      fetchReservation();
    }
    return () => {
      ignore = true;
      resetReservationToBeModified();
    };
  }, [reservation]);

  useEffect(() => {
    let ignore = false;

    async function fetchNotifies() {
      const { notify } = await getNotify();

      const { day, serviceTypeId } = reservation;
      const filteredNotifies = notify?.filter((singleNotify) => singleNotify.specs.service_type_id === serviceTypeId && singleNotify.specs.day === day);

      if (!ignore && filteredNotifies) {
        setNotifies(filteredNotifies);
      }
    }

    if (reservation) {
      fetchNotifies();
    }
    return () => {
      ignore = true;
    };
  }, [reservation]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/venues/${reservation?.venue?.id}`);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    reset();
  }, []);

  // reset the resyNav title and subTitle
  setNavTitle(null);
  setNavSubTitle(null);

  const isModal = window.self !== window.top;

  return (
    <ConfirmationPage
      Analytics={Analytics}
      cancel={handleReservationCancel}
      closeWidget={closeWidget}
      closeWindow={closeWindow}
      day={reservation?.day}
      flowContext={POST_BOOKING}
      isModal={isModal}
      navigate={navigate}
      notifies={notifies}
      pathBuilder={pathBuilder}
      preferredLanguage={preferredLanguage}
      reservation={reservation}
      returnUrl={returnUrl}
      src={src}
      time={reservation?.time}
      viewPortHeight={viewPortHeight}
    />
  );
};

ConfirmationController.propTypes = {
  Analytics: PropTypes.shape({}).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  preferredLanguage: PropTypes.string.isRequired,
  reset: PropTypes.func.isRequired,
  resetReservationToBeModified: PropTypes.func.isRequired,
  returnUrlFromBookingMetadata: PropTypes.string,
  setNavSubTitle: PropTypes.func.isRequired,
  setNavTitle: PropTypes.func.isRequired,
  setReservationToBeModified: PropTypes.func.isRequired,
  setRouteEventPath: PropTypes.func.isRequired,
};

ConfirmationController.defaultProps = {
  returnUrlFromBookingMetadata: undefined,
};

const mapStateToProps = (state) => {
  const isAuthenticated = selectIsAuthenticated(state);
  return {
    isAuthenticated,
    returnUrlFromBookingMetadata: state?.resyRouter?.returnUrl,
  };
};

const mapDispatchToProps = ({
  reset,
  resetReservationToBeModified,
  setNavSubTitle,
  setNavTitle,
  setReservationToBeModified,
  setRouteEventPath,
});

export { ConfirmationController as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(ConfirmationController);
